import { Helmet } from 'react-helmet-async';
import React, { useState, useEffect, Suspense } from 'react';
import HeroSection from './home/HeroSection';
import { calculatorCategories } from '../data/calculatorCategories';
import { useLanguage } from '../i18n/LanguageContext';

// Lazy load components
const FavoritesSection = React.lazy(() => import('./home/FavoritesSection'));
const PopularCalculators = React.lazy(() => import('./home/PopularCalculators'));
const CategoryList = React.lazy(() => import('./home/CategoryList'));
const FeaturesSection = React.lazy(() => import('./home/FeaturesSection'));
const Announcements = React.lazy(() => import('./home/Announcements'));
const PopularNumbers = React.lazy(() => import('./home/PopularNumbers'));
const PopularGoldLinks = React.lazy(() => import('./home/PopularGoldLinks'));
const ThemeToggle = React.lazy(() => import('./home/ThemeToggle'));
const PopularPercentageSearches = React.lazy(() => import('./calculators/PopularPercentageSearches'));
const PopularSqrtLinks = React.lazy(() => import('./home/PopularSqrtLinks'));

interface HomePageProps {
  setActiveCalculator: (id: string) => void;
  onCalculatorSelect?: (id: string) => void;
}

const HomePage: React.FC<HomePageProps> = ({ setActiveCalculator, onCalculatorSelect }) => {
  const { t, getLink, language } = useLanguage();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchResults, setSearchResults] = useState<Array<{
    id: string;
    name: string;
    icon: React.ReactNode;
    description?: string;
  }>>([]);

  // Favori hesaplayıcılar için state
  const [favorites, setFavorites] = useState<string[]>([]);
  const [favoritesSectionVisible, setFavoritesSectionVisible] = useState<boolean>(false);

  // Mirroring logic from App.tsx/translations for consistency
  const pageTitle = language === 'nl' ? 'Hesaplama.Vip - Online Rekenmachines' : 'Hesapla ya da Hesaplama - Hesaplama.Vip';
  const pageDescription = language === 'nl'
    ? 'Verzameling van moderne rekenmachines voor al uw dagelijkse berekeningen. Percentage, goud, financiën en wiskunde op één plek.'
    : 'Modern hesaplama araçları koleksiyonu ile hızlı ve doğru hesaplamalar yapın. Yüzde, altın, finans ve matematik hesaplayıcıları bir arada.';

  // Favori hesaplayıcıları localStorage'dan yükle
  useEffect(() => {
    const storedFavorites = localStorage.getItem('favoriteCalculators');
    if (storedFavorites) {
      const parsedFavorites = JSON.parse(storedFavorites);
      setFavorites(parsedFavorites);
      setFavoritesSectionVisible(parsedFavorites.length > 0);
    }
  }, []);

  // Favori ekle/çıkar
  const toggleFavorite = (id: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    let newFavorites: string[];

    if (favorites.includes(id)) {
      newFavorites = favorites.filter(favId => favId !== id);
    } else {
      newFavorites = [...favorites, id];
    }

    setFavorites(newFavorites);
    localStorage.setItem('favoriteCalculators', JSON.stringify(newFavorites));
    setFavoritesSectionVisible(newFavorites.length > 0);
  };

  const getFavoriteCalculators = () => {
    const allowedNlIds = ['percentage', 'pronunciation', 'goldCalculator', 'sqrt', 'dollarEuro'];
    const allCalculators = calculatorCategories.flatMap(category =>
      category.calculators
        .filter(calc => language === 'tr' || allowedNlIds.includes(calc.id))
        .map(calc => ({
          ...calc,
          categoryId: category.id,
          categoryTitle: category.title
        }))
    );
    return allCalculators.filter(calc => favorites.includes(calc.id));
  };

  const handleNavigate = (id: string) => {
    if (onCalculatorSelect) {
      onCalculatorSelect(id);
    } else {
      setActiveCalculator(id);
    }
  };

  // Arama sonuçlarını filtrele
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    const term = searchTerm.toLowerCase();

    const allowedNlIds = ['percentage', 'pronunciation', 'goldCalculator', 'sqrt', 'dollarEuro'];

    // Tüm hesaplayıcıları bir araya getir
    const allCalculators = calculatorCategories.flatMap(cat =>
      cat.calculators
        .filter(calc => language === 'tr' || allowedNlIds.includes(calc.id))
        .map(calc => {
          // Translate name if possible
          const translatedName = t(`tools.${calc.id}`);
          const localizedName = translatedName !== `tools.${calc.id}` ? translatedName : calc.name;

          return {
            ...calc,
            name: localizedName,
            description: calc.description || localizedName
          };
        })
    );

    const uniqueCalculators = Array.from(
      new Map(allCalculators.map(calc => [calc.id, calc])).values()
    );

    const filtered = uniqueCalculators.filter(calc =>
      calc.name.toLowerCase().includes(term) ||
      (calc.description && calc.description.toLowerCase().includes(term))
    );

    setSearchResults(filtered);
  }, [searchTerm, t, language]);

  const LoadingFallback = () => <div className="h-24 flex items-center justify-center text-cyan-500/50">Yükleniyor...</div>;

  return (
    <div className="min-h-screen py-6">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        {/* Canonical handled by App.tsx, but title specifically needs enforcement here */}
      </Helmet>
      <HeroSection
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        searchResults={searchResults}
        onNavigate={handleNavigate}
        getUrlFromId={(id) => getLink(id)}
      />

      {!searchTerm && (
        <>
          {favoritesSectionVisible && (
            <Suspense fallback={<LoadingFallback />}>
              <FavoritesSection
                favorites={favorites}
                getFavoriteCalculators={getFavoriteCalculators}
                toggleFavorite={toggleFavorite}
                onNavigate={handleNavigate}
                getUrlFromId={(id) => getLink(id)}
              />
            </Suspense>
          )}

          <Suspense fallback={<LoadingFallback />}>
            <PopularCalculators onNavigate={handleNavigate} getUrlFromId={(id) => getLink(id)} />
          </Suspense>

          <div className="min-h-[400px]">
            <Suspense fallback={<LoadingFallback />}>
              <CategoryList
                favorites={favorites}
                toggleFavorite={toggleFavorite}
                onNavigate={handleNavigate}
                getUrlFromId={(id) => getLink(id)}
              />
            </Suspense>
          </div>

          {language === 'tr' && (
            <>
              <Suspense fallback={<LoadingFallback />}>
                <FeaturesSection />
              </Suspense>

              <Suspense fallback={<LoadingFallback />}>
                <Announcements />
              </Suspense>

              <Suspense fallback={<LoadingFallback />}>
                <PopularNumbers />
              </Suspense>

              <Suspense fallback={<LoadingFallback />}>
                <PopularGoldLinks />
              </Suspense>
            </>
          )}

          <Suspense fallback={<LoadingFallback />}>
            <PopularPercentageSearches />
          </Suspense>

          <Suspense fallback={<LoadingFallback />}>
            <PopularSqrtLinks />
          </Suspense>

          <div className="text-center mt-12 text-gray-600 dark:text-gray-400 text-sm flex justify-center gap-6 flex-wrap">
            <a href={getLink('about')} className="hover:text-cyan-500 transition-colors">{t('home.about')}</a>
            <a href={getLink('contact')} className="hover:text-cyan-500 transition-colors">{t('home.contact')}</a>
            <a href={getLink('privacyPolicy')} className="hover:text-cyan-500 transition-colors">{t('home.privacy')}</a>
          </div>

          <Suspense fallback={<div className="h-10"></div>}>
            <ThemeToggle />
          </Suspense>
        </>
      )}
    </div>
  );
};

export default HomePage;